import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ArrowDown from "../../common/icons/ArrowDown";
import { frequentProductList } from "../../../redux/Thunk/Product/ProductThunks";
import toast from "react-hot-toast";
import ProductListType from "../../common/ProductListType";
import { debounce } from "lodash";
import { useOutsideClick } from "../../common/useOutsideClick";
import { createCustomProduct } from "../../../redux/Thunk/Product/ProductThunks";
import { extentions } from "../../../utils/constants";
import PortalTooltip from "../../common/PortalTooltip";


const List = ({ label, SID, storeID, data, handleSetProduct }) => {
  const findStoreStock = (product, item) => {
    const id = storeID ? storeID : SID;
    const storeProduct = item?.product?.storeProducts?.find(
      (storeItem) => storeItem.SID === id
    );
    return storeProduct?.storeStock;
  };
  return (
    <div className="mt-3">
      <h4 className="text-primary-400 text-[11px] font-semibold uppercase tracking-tight mb-2 px-1">
        {label}
      </h4>
      <div className="max-h-[300px] overflow-y-auto space-y-1 scrollbar-hide focus:outline-none">
        {data?.length > 0 ? (
          data?.map((item, idx) => {
            return (
              <div
                key={item?.id || item?.product?.IID || item?.CPID || `idx-${idx}`}
                className="flex items-start justify-between cursor-pointer p-2 hover:bg-neutral-50 rounded-md transition-all duration-150 border border-transparent hover:border-primary-100"
                onClick={() => handleSetProduct(item)}
              >
                <ProductListType
                  product={item?.product || item}
                  stock={
                    item?.storeStock || item?.product?.storeStock
                  }
                  findStoreStock={(product) => findStoreStock(product, item)}
                />
              </div>
            );
          })
        ) : (
          <p className="text-gray-400 text-[13px] italic py-2 px-1 text-center">No product found</p>
        )}
      </div>
    </div>
  )
}

const ProductList = ({
  name,
  values,
  onChange,
  setFieldValue,
  unitName,
  index,
  selectedProducts,
  setSelectedProducts,
  setBillingData,
  readOnly,
  allProducts,
  isCompact = false,
}) => {
  const [activeTab, setActiveTab] = useState("search");
  const frequentProducts = useSelector(
    (state) => state?.product?.frequentProduct
  );
  const doctorId = useSelector((state) => state?.doctor?.doctorDetails?.doctor?.DID || null);
  const is_pin_b = useSelector((state) => state?.doctor?.doctorDetails?.doctor?.is_pin_b);
  const idStore = useSelector((state) => state?.doctor?.doctorDetails?.doctor?.SID || null);
  const SID = useSelector((state) => state.store.storeDetail?.store?.SID);
  const storeID = useSelector(
    (state) => state.doctor.doctorDetails?.doctor?.SID
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAllDrugs, setShowAllDrugs] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customProductData, setCustomProductData] = useState({ name: "", drug: "", isEditingMode: false });
  const [tempCustomName, setTempCustomName] = useState("");
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);

  const dispatch = useDispatch();
  useEffect(() => {
    setProducts(frequentProducts);
  }, [frequentProducts]);

  const handleClickOutside = (event) => {
    if (dropdownRef?.current && !dropdownRef?.current.contains(event?.target)) {
      setIsOpen(false);
      setIsTypeDropdownOpen(false);
    }
  };
  const dropdownRef = useOutsideClick(handleClickOutside);


  const handleSetProduct = (product) => {
    const { productName, uom, lookupMargin, pricing, type, IID, drugs, tags, drugTags, units, isCustom } =
      product?.product || product;

    const isCustomProduct = isCustom || (IID && String(IID).startsWith("CP"));

    const {
      mrp: productMrp = 0,
      gst: productGst = 0,
    } = pricing || {};

    const {
      discount: productDiscount = 0,
      type: lookupMarginType = ""
    } = lookupMargin || {};

    const targetIID = IID || product?.IID || product?.id;
    const targetName = productName || product?.productName;

    let isAlreadyAdded = allProducts?.some((p) => {
      if (p === values) return false;
      const pIID = p?.productId || p?.IID;
      const pName = p?.productName;
      if (targetIID && pIID && String(targetIID) === String(pIID)) return true;
      if (targetName && pName && String(targetName).trim().toLowerCase() === String(pName).trim().toLowerCase()) return true;
      return false;
    });

    if (isAlreadyAdded) {
      toast.error("Product already added in prescription or return list!");
      return;
    }

    const discountofProduct = ((productMrp * productDiscount) / 100).toFixed(2);
    const netAmount = (productMrp - discountofProduct).toFixed(2);

    setFieldValue(name, productName);
    // setFieldValue(unitName, units);
    setFieldValue(`${index}.mrp`, parseFloat(productMrp));
    setFieldValue(`${index}.gst`, parseFloat(productGst));
    setFieldValue(`${index}.discount`, parseFloat(productDiscount));
    setFieldValue(`${index}.netAmt`, parseFloat(netAmount));
    setFieldValue(`${index}.type`, type);
    setFieldValue(`${index}.units`, units);
    setFieldValue(`${index}.productId`, IID);
    setFieldValue(`${index}.isCustom`, isCustomProduct);
    setFieldValue(`${index}.drugs`, drugs);
    setFieldValue(`${index}.tags`, tags || product?.tags || []);
    setFieldValue(`${index}.drugTags`, drugTags || product?.drugTags || []);
    setFieldValue(`${index}.uom`, uom);
    setFieldValue(`${index}.units`, units || uom || 1);
    const id = storeID ? storeID : SID;
    const storeProduct = product?.storeStock
      ? product
      : product?.product?.storeProducts?.find(
        (storeItem) => storeItem.SID === id
      );
    setFieldValue(
      `${index}.qty`,
      storeProduct?.storeStock === undefined
        ? 0
        : Number(storeProduct?.storeStock)
    );

    const isReturnProduct = Boolean(values?.isReturn || String(index).startsWith("returnProducts"));
    const isNaDuration = values.isNA || values.dosageMode === 'NA' || values.rxDaysDisplay === 'NA';
    const hasRxDays = isNaDuration ? true : (values.rxDays > 0 && !isNaN(values.rxDays));
    if (isReturnProduct) {
      const returnQty = Math.max(1, Number(values?.orderQty) || 1);
      setFieldValue(`${index}.orderQty`, returnQty);
    } else if (!hasRxDays) {
      const mode = values.dosageMode || (values.isSOS ? "SOS" : values.isNA ? "NA" : values.isReducingDose ? "REDUCING" : values.isPeriodic ? "PERIODIC" : "REGULAR");
      if (mode === "SOS") {
        setFieldValue(`${index}.rxDays`, 1);
        setFieldValue(`${index}.rxDaysDisplay`, "1");
        setFieldValue(`${index}.rxUnits`, 1);
        setFieldValue(`${index}.orderQty`, 1);
      } else if (mode === "NA") {
        setFieldValue(`${index}.rxDays`, 0);
        setFieldValue(`${index}.rxDaysDisplay`, "NA");
        setFieldValue(`${index}.rxUnits`, 0);
        setFieldValue(`${index}.orderQty`, 1);
      } else {
        setFieldValue(`${index}.rxDays`, 0);
        setFieldValue(`${index}.rxDaysDisplay`, "");
        setFieldValue(`${index}.rxUnits`, 0);
        setFieldValue(`${index}.orderQty`, 0);
      }
    } else {
      const rxDays = values.rxDays;
      let totalTimes = (Number(values.morningTime) || 0) + (Number(values.midDay) || 0) + (Number(values.eveningTime) || 0) + (Number(values.night) || 0);
      const rxUnits = rxDays * totalTimes;
      setFieldValue(`${index}.rxUnits`, rxUnits);
      const isCalcType = extentions[type]?.calc !== false;
      const effectiveUom = Number(uom || units || 1) || 1;
      let computedOrderQty = Number(values.orderQty) || 0;
      if (isCustomProduct) {
        computedOrderQty = 0;
      } else if (!isCalcType || effectiveUom <= 0 || totalTimes === 0) {
        computedOrderQty = 1;
      } else {
        computedOrderQty = Math.ceil(rxUnits / effectiveUom) || 1;
      }
      setFieldValue(`${index}.orderQty`, computedOrderQty);
      const orderUnits = isCustomProduct ? 0 : computedOrderQty * effectiveUom;
      setFieldValue(`${index}.orderUnits`, orderUnits);
      setFieldValue(`${index}.balanceUnits`, Math.max(0, rxUnits - orderUnits));
    }

    setFieldValue(`${index}.lookupMargin.type`, lookupMarginType);
    onChange(productName);
    setIsOpen(false);
    setSearchTerm("");
    let temselectedProducts = selectedProducts;
    if (temselectedProducts && setSelectedProducts) {
      if (Object.keys(temselectedProducts)?.length > 0) {
        const tempIndex = Number(index.split(".")?.[1]);
        let assinedData = Object.keys(temselectedProducts);
        if (assinedData[tempIndex] === undefined) {
          const updatedProducts = { ...temselectedProducts };
          const productIndex = IID;
          updatedProducts[productIndex] = product;

          setSelectedProducts(updatedProducts);
        } else {
          const updatedData = updateKeyName(
            temselectedProducts,
            assinedData[tempIndex],
            IID,
            product
          );

          setSelectedProducts(updatedData);
        }
      } else {
        const updatedProducts = { ...temselectedProducts };
        const productIndex = IID;
        updatedProducts[productIndex] = product;
        setSelectedProducts(updatedProducts);
      }
    }

    // Update the form
    setFieldValue(`${index}.morningTime`, 0);
    setFieldValue(`${index}.midDay`, 0);
    setFieldValue(`${index}.eveningTime`, 0);
    setFieldValue(`${index}.night`, 0);
    const foodRelatedTypes = ["Tablet", "Capsules", "Syrup", "Suspension"];
    const isFoodRelated = foodRelatedTypes.includes(type);
    const defaultTakenTime = isFoodRelated ? "A/F" : "AD";
    const defaultTakenTimeIndex = isFoodRelated ? 0 : 6;
    setFieldValue(`${index}.takenTime`, defaultTakenTime);
    setFieldValue(`${index}.takenTimeIndex`, defaultTakenTimeIndex);
    setFieldValue(`${index}.extra`, 0);
    setFieldValue(`${index}.extraIndex`, 0);
    setFieldValue(`${index}.rxDaysDisplay`, "");
    setFieldValue(`${index}.rxDays`, 0);
    setFieldValue(`${index}.rxUnits`, 0);
    if (isReturnProduct) {
      const returnQty = Math.max(1, Number(values?.orderQty) || 1);
      setFieldValue(`${index}.orderQty`, returnQty);
    } else {
      setFieldValue(`${index}.orderQty`, 0);
      setFieldValue(`${index}.orderUnits`, 0);
      setFieldValue(`${index}.balanceUnits`, 0);
    }
    if (setBillingData) {
      setBillingData(0);
    }
  };

  // function for replace old data
  function updateKeyName(obj, oldKey, newKey, newObj) {
    const updatedObject = {};
    for (const key in obj) {
      // Check if the current key is the one to update
      if (String(key) === String(oldKey)) {
        updatedObject[newKey] = newObj; // Replace with the new key
      } else {
        updatedObject[key] = obj[key]; // Keep other keys unchanged
      }
    }
    return updatedObject;
  }

  // const handleCustomSubmit = () => {
  //   if (!tempCustomName || !tempCustomName.trim()) {
  //     toast.error("Please enter a custom product name");
  //     return;
  //   }
  //   const payload = {
  //     productName: tempCustomName.toUpperCase().trim(),
  //     uom: 1,
  //     lookupMargin: { discount: 0, type: "Custom" },
  //     pricing: { mrp: 0, gst: 0 },
  //     type: "Other",
  //     IID: null,
  //     drugs: [],
  //     units: 1,
  //     isCustom: true,
  //     storeStock: 0,
  //     qty: 0
  //   };
  //   handleSetProduct(payload);
  //   setTempCustomName("");
  // };

  const debouncedSearch = useMemo(
    () =>
      debounce((id, value) => {
        dispatch(frequentProductList({ id, productName: value, search: value }));
      }, 300),
    [dispatch]
  );

  const handleChange = (e) => {
    let value = e.target.value;
    setSearchTerm(value);
    const id = doctorId ? doctorId : idStore || SID;
    debouncedSearch(id, value);
  };

  const handleCustomAction = async (requestInventory) => {
    const actionPayload = {
      name: customProductData.name || searchTerm,
      drug: customProductData.drug,
      type: customProductData.type || "Other",
      inventoryRequest: requestInventory,
    };
    try {
      const response = await dispatch(createCustomProduct(actionPayload)).unwrap();
      if (response && response.data) {
        handleSetProduct(response.data);
      }
      setShowCustomModal(false);
      setCustomProductData({ name: "", drug: "", isEditingMode: false });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="w-full h-full" ref={dropdownRef}>
      <div className="h-full relative">
        <div
          onClick={() => {
            if (readOnly) return;
            if (values?.productName) {
              const newState = !isOpen;
              setIsOpen(newState);
              if (newState) {
                if (activeTab !== "search") setActiveTab("search");
                
                // If it's a naked product (AI injected without ID), auto-search
                if (!values.productId && !values.IID) {
                  setSearchTerm(values.productName);
                  const id = doctorId ? doctorId : idStore || SID;
                  debouncedSearch(id, values.productName);
                }
                
                setTimeout(() => {
                  const input = document.getElementById("search");
                  input?.focus();
                }, 0);
              }
            } else if (!isOpen) {
              setIsOpen(true);
            }
          }}
          className={`flex ${
            values?.productName
              ? isCompact
                ? "min-h-[52px] lg:min-h-[66px] h-auto py-2 lg:py-2.5"
                : "min-h-[66px] lg:min-h-[82px] h-auto py-2.5 lg:py-3"
              : isCompact
                ? "h-[44px] lg:h-[48px] py-1.5 lg:py-2"
                : "min-h-[54px] lg:h-[82px] py-2 lg:py-2.5"
          } text-[16px] items-center justify-between gap-2.5 text-slate-800 border border-slate-200 bg-white px-2.5 lg:px-[12px] w-full outline-none cursor-pointer transition-all duration-200 ${isOpen ? "rounded-t-lg border-slate-300 shadow-sm" : "rounded-md hover:border-slate-300"}`}
        >
          {values?.productName ? (
            <div className="flex items-start cursor-pointer flex-1 min-w-0">
              <div className="text-left w-full min-w-0">
                <PortalTooltip text={values.productName}>
                  <h5 className="text-[13.5px] lg:text-[15px] font-semibold text-primary-700 leading-tight truncate">
                    {values.productName || ""}
                  </h5>
                </PortalTooltip>
                <p className="text-[11px] text-primary-500 mt-0.5 truncate uppercase">
                  {values.type} • {values.uom} {extentions[values?.type]?.uomExt || extentions[values?.type]?.extention || ""} {is_pin_b && values.lookupMargin?.type && `• ${values.lookupMargin?.type}`} • <span className={`${Number(values?.qty) > 6
                    ? "font-semibold text-success-600"
                    : Number(values?.qty) === 0
                      ? "font-semibold text-primary-600"
                      : "font-semibold text-yellow-700"
                    }`}>Stock: {values.productName ? values?.qty : ""}</span>
                  {(values.isCustom || String(values.productId || values.IID).startsWith("CP") || (values.productId === null && Number(values.orderQty) === 0)) && (
                    <span className="ml-2 bg-amber-100 text-amber-800 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase border border-amber-200">
                      Custom
                    </span>
                  )}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  {values.isAlternate && values.originalName && (
                    <PortalTooltip 
                      text={`Safe substitute matching composition of missing brand: ${values.originalName}`}
                      className="relative inline-flex flex-shrink-0"
                      align="left"
                    >
                      <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase border border-emerald-200 inline-flex items-center flex-shrink-0">
                        Substitute
                      </span>
                    </PortalTooltip>
                  )}
                  {values.productName && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAllDrugs(!showAllDrugs);
                      }}
                      className="text-primary-500 text-[10px] uppercase font-semibold bg-primary-50 px-1.5 py-0.5 rounded border border-primary-100 hover:bg-primary-100 transition-colors whitespace-nowrap flex-shrink-0 cursor-pointer"
                    >
                      INFO
                    </button>
                  )}
                  <p className="text-[11px] text-primary-400 truncate flex-1 max-w-[140px] italic">
                    {values.drugs && values.drugs.length > 0
                      ? values.drugs
                        .slice(0, 3)
                        .map((drug, index) => drug?.name ? drug?.name : drug)
                        .join(", ") +
                      (values.drugs.length > 3 ? "..." : "")
                      : ""}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-center items-center flex-1 w-full h-full">
              <input
                id="search"
                type="text"
                placeholder="Search or Select Product"
                value={searchTerm}
                onChange={(e) => {
                  handleChange(e);
                  if (!isOpen) setIsOpen(true);
                  if (activeTab !== "search") setActiveTab("search");
                }}
                onFocus={() => {
                  setIsFocused(true);
                  if (!isOpen) setIsOpen(true);
                  if (activeTab !== "search") setActiveTab("search");
                }}
                onBlur={() => setIsFocused(false)}
                onClick={(e) => {
                  if (!isOpen) setIsOpen(true);
                  e.stopPropagation();
                }}
                className={`w-full h-full text-primary-700 text-[15px] font-semibold bg-transparent outline-none placeholder:text-primary-700 placeholder:opacity-50 transition-all duration-300 ${(searchTerm || isFocused) ? "text-left pl-2" : "text-center"}`}
              />
            </div>
          )}
          {values?.productName && !selectedProducts?.[values?.productId]?.product?.isCustom && (
            <div className="flex items-center gap-3 h-full flex-shrink-0 ml-auto">
              <div className="flex flex-col items-end">
                <span className="text-m font-semibold text-primary-700">
                  ₹{parseFloat(values?.netAmt || 0).toFixed(2)}
                </span>
                <span className="text-xs text-primary-500 line-through">
                  ₹{parseFloat(values?.mrp || 0).toFixed(2)}
                </span>
                <span className="text-xs font-medium text-success-600">
                  {Math.round(values?.discount || 0)}% off
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Conditionally render dropdown content */}
        {isOpen && (
          <div className="bg-white w-full mx-auto absolute top-full left-0 right-0 z-[999] rounded-b-lg border-b border-x border-primary-200 shadow-xl mt-[1px] max-h-[250px] overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#CCBDBD transparent' }}>
            <div className="bg-primary-50/50 border-b border-primary-100">
              <ul className="flex items-center w-full p-1 gap-1">
                <li
                  className={`flex-1 text-center py-1.5 px-4 cursor-pointer rounded text-[13px] font-semibold transition-all duration-200 ${activeTab === "search" ? "bg-white text-primary-700 shadow-sm" : "text-primary-400 hover:bg-white/50"}`}
                  onClick={() => setActiveTab("search")}
                >
                  Search
                </li>
                <li
                  className={`flex-1 text-center py-1.5 px-4 cursor-pointer rounded text-[13px] font-semibold transition-all duration-200 ${activeTab === "frequent" ? "bg-white text-primary-700 shadow-sm" : "text-primary-400 hover:bg-white/50"}`}
                  onClick={() => setActiveTab("frequent")}
                >
                  Frequent
                </li>
              </ul>
            </div>
            <div className="p-2 pt-0">
              {activeTab === "frequent" ? (
                <List label={"Frequent Items"} SID={SID} storeID={storeID} data={products?.frequentProducts} handleSetProduct={handleSetProduct} />
              ) : (
                <>
                  {values?.productName && (
                    <div className="mt-3 px-1">
                      <input
                        id="search"
                        type="text"
                        placeholder="Search Product or Drug..."
                        value={searchTerm}
                        onChange={(e) => handleChange(e)}
                        className="w-full p-2 border border-gray-200 bg-primary-50 focus:outline-none rounded-md text-sm"
                      />
                    </div>
                  )}

                  {/* Custom Product Block */}
                  {searchTerm && searchTerm.trim() !== "" && products?.searchProducts?.length === 0 && (
                    <div className="mt-3 mx-1 bg-red-50 border border-red-100 rounded-lg p-3 text-center">
                      <p className="text-gray-600 text-xs mb-1">Oops! This product is not listed</p>
                      <p className="text-primary-700 font-bold text-lg leading-tight uppercase">"{searchTerm}"</p>
                      <p className="text-gray-500 text-xs mt-1 mb-2">Add it now</p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCustomProductData({ name: searchTerm.toUpperCase(), drug: "", isEditingMode: false });
                          setShowCustomModal(true);
                        }}
                        className="bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold py-1.5 px-4 rounded transition-colors"
                      >
                        Add Custom Product
                      </button>
                    </div>
                  )}

                  {/* Search List Section */}
                  <List label={"Search Results"} SID={SID} storeID={storeID} data={products.searchProducts} handleSetProduct={handleSetProduct} />

                  {/* Drug Alternate List Section */}
                  {products?.alternateProducts?.length > 0 && (
                    <List label={"Exact Substitutes List"} SID={SID} storeID={storeID} data={products?.alternateProducts} handleSetProduct={handleSetProduct} />
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Drugs Modal/Popover */}
      {showAllDrugs && (() => {
        const fullProduct = selectedProducts?.[values.productId || values.IID] || {};
        const manufacturerVal = fullProduct?.manufacturer || fullProduct?.product?.manufacturer || "";
        const stockVal = values.qty !== undefined ? values.qty : (fullProduct?.qty || fullProduct?.productStock || 0);
        const uomVal = values.uom || fullProduct?.uom || fullProduct?.product?.uom || values.units || 1;
        const uomType = extentions[values.type]?.uomExt || extentions[values.type]?.extention || "";

        return (
          <div 
            onClick={() => setShowAllDrugs(false)} 
            className="fixed inset-0 bg-[#0a1128]/35 backdrop-blur-xs flex items-center justify-center z-[9999] p-4 transition-all duration-300"
          >
            <div 
              onClick={(e) => e.stopPropagation()} 
              className="bg-white/95 backdrop-blur-lg border border-white/20 rounded-2xl p-6 max-w-md w-full shadow-2xl relative max-h-[85vh] overflow-y-auto animate-scale-in"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4 pb-2 border-b border-primary-100/60">
                <div>
                  <h3 className="text-[10px] font-bold text-primary-400 uppercase tracking-wider">Product Information</h3>
                  <h2 className="text-base font-extrabold text-primary-950 mt-0.5 leading-snug">{values.productName || "Product Detail"}</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAllDrugs(false)}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-50 hover:bg-primary-100 text-primary-500 hover:text-primary-700 transition-all font-bold text-sm shadow-3xs cursor-pointer"
                >
                  ✕
                </button>
              </div>

              {/* Grid Specs */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="p-3 bg-primary-50/40 rounded-xl border border-primary-100/20">
                  <span className="block text-[9px] font-bold text-primary-400 uppercase tracking-wider">Type</span>
                  <span className="text-[12px] font-extrabold text-primary-800">{values.type || "N/A"}</span>
                </div>
                <div className="p-3 bg-primary-50/40 rounded-xl border border-primary-100/20">
                  <span className="block text-[9px] font-bold text-primary-400 uppercase tracking-wider">Packaging (UOM)</span>
                  <span className="text-[12px] font-extrabold text-primary-800">
                    {uomVal} {uomType}
                  </span>
                </div>
                <div className="p-3 bg-primary-50/40 rounded-xl border border-primary-100/20">
                  <span className="block text-[9px] font-bold text-primary-400 uppercase tracking-wider">Store Stock</span>
                  <span className={`text-[12px] font-extrabold ${Number(stockVal) > 5 ? 'text-green-600' : 'text-amber-600'}`}>
                    {stockVal} units
                  </span>
                </div>
                <div className="p-3 bg-primary-50/40 rounded-xl border border-primary-100/20">
                  <span className="block text-[9px] font-bold text-primary-400 uppercase tracking-wider">Manufacturer</span>
                  <span className="text-[12px] font-extrabold text-primary-800 truncate block" title={manufacturerVal}>{manufacturerVal || "N/A"}</span>
                </div>
              </div>

              {/* Drug Composition */}
              {values.drugs && values.drugs.length > 0 && (
                <div>
                  <h4 className="text-[9px] font-bold text-primary-400 uppercase tracking-wider mb-2">Salt Composition</h4>
                  <div className="space-y-1.5">
                    {values.drugs.map((drug, index) => (
                      <div
                        key={index}
                        className="px-3 py-2 bg-primary-50/60 rounded-lg text-primary-700 text-xs font-bold border border-primary-100/40 shadow-3xs"
                      >
                        {drug?.name ? drug?.name : drug}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Custom Product Modal */}
      {showCustomModal && (
        <div className="fixed inset-0 bg-[#0a1128]/85 backdrop-blur-sm flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-[95%]">
            {!customProductData.isEditingMode ? (
              <>
                <div className="text-center mb-6">
                  <h3 className="text-primary-700 text-sm font-medium mb-1">You are adding</h3>
                  <h2 className="text-2xl font-bold text-primary-800 uppercase break-words">"{customProductData.name}"</h2>
                  <h3 className="text-primary-700 text-sm font-medium mt-1">in the prescription!</h3>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="relative">
                    <label className="block text-primary-700 text-xs font-semibold mb-2">
                      Product Type
                    </label>
                    <div
                      className="w-full border border-primary-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none bg-primary-50 flex items-center justify-between cursor-pointer hover:border-primary-400 transition-colors shadow-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsTypeDropdownOpen(!isTypeDropdownOpen);
                      }}
                    >
                      <span className="text-primary-800 font-medium">{customProductData.type || "Other"}</span>
                      <span className={`transition-transform duration-200 ${isTypeDropdownOpen ? "rotate-180" : ""}`}>
                        <ArrowDown />
                      </span>
                    </div>

                    {isTypeDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-primary-100 rounded-xl shadow-2xl z-[10000] max-h-[220px] overflow-y-auto overflow-x-hidden scrollbar-hide py-1 transform animate-in fade-in slide-in-from-top-2 duration-200">
                        {Object.keys(extentions).map(type => (
                          <div
                            key={type}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCustomProductData({ ...customProductData, type: type });
                              setIsTypeDropdownOpen(false);
                            }}
                            className={`px-4 py-2 cursor-pointer text-sm transition-colors ${customProductData.type === type ? "bg-primary-600 text-white font-bold" : "text-primary-700 hover:bg-primary-50"}`}
                          >
                            {type}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-primary-700 text-xs font-semibold mb-2">
                      Associated Drug
                    </label>
                    <input
                      type="text"
                      value={customProductData.drug}
                      onChange={(e) => setCustomProductData({ ...customProductData, drug: e.target.value })}
                      className="w-full border border-primary-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-500 bg-primary-50"
                      placeholder="E.g. Paracetamol"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCustomModal(false);
                      }}
                      className="flex-1 py-2 px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTempCustomName(customProductData.name);
                        setCustomProductData({ ...customProductData, isEditingMode: true });
                      }}
                      className="flex-1 py-2 px-3 bg-primary-50 hover:bg-primary-100 text-primary-700 border border-primary-200 text-xs font-semibold rounded-lg transition-colors"
                    >
                      Edit Name
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCustomAction(false);
                      }}
                      className="flex-1 py-2.5 px-2 bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold rounded-lg transition-colors"
                    >
                      Add Product
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCustomAction(true);
                      }}
                      className="flex-1 py-2.5 px-2 bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold rounded-lg transition-colors"
                    >
                      Add & Request Inventory
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="mb-6">
                  <label className="block text-primary-700 text-sm font-semibold mb-2">
                    Edit Product Name
                  </label>
                  <input
                    type="text"
                    value={tempCustomName}
                    onChange={(e) => setTempCustomName(e.target.value.toUpperCase())}
                    className="w-full border border-primary-200 rounded-lg px-3 py-2.5 text-sm uppercase focus:outline-none focus:border-primary-500 bg-primary-50"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCustomProductData({ ...customProductData, isEditingMode: false });
                    }}
                    className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCustomProductData({ ...customProductData, name: tempCustomName, isEditingMode: false });
                    }}
                    className="flex-1 py-2.5 px-4 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg transition-colors"
                  >
                    Save
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductList;
